/**
 * The animated half of the resolution funnel.
 *
 * `resolveRoll` in core is for rolls the engine makes: it throws the dice and
 * reads them in one call. The 3D tray cannot work that way — the player watches
 * real dice land, so the system has to be asked what to throw BEFORE the throw
 * and what it meant AFTER, with an animation in between. This module is that
 * round trip, so no component has to know it is a round trip.
 *
 * WHY THIS EXISTS AND NOT JUST `checkNotation` AT EACH CALL SITE: the literal
 * `'1d20'` was never the whole problem. Every one of these call sites also
 * hard-coded `=== 20` for a crit and `=== 1` for a fumble, and several compared
 * a total to a DC by hand. Replacing the string and leaving the comparisons
 * would move the assumption rather than remove it — a system where the good end
 * of the die is 1 would still light up the crit glow on a 20.
 *
 * ADVANTAGE: rolling twice and keeping one is a d20 idea, and the adapter has
 * no concept of it. It is applied here only when the system's core resolution
 * is a single die, which is exactly when the idea means anything; a 2d6 system
 * asked for advantage gets a straight roll rather than a silent mistranslation.
 */

import {
  checkNotation,
  interpretRolled,
  succeeded,
  type ResolvedRoll,
  type RollContext,
} from '@machina/core';

import type { DiceResult } from '../components/DiceRoller';
import { useDiceStore } from '../stores/diceStore';

export type RollMode = 'normal' | 'advantage' | 'disadvantage';

export interface AnimatedCheck {
  /** What the system made of the dice. */
  resolved: ResolvedRoll;
  /** The die the system read, before modifiers — the number a crit is judged on. */
  natural: number;
  /** Natural plus modifier. */
  total: number;
  /** Display notation for the chat log, e.g. `1d20+3 (Athletics)`. */
  notation: string;
  isCrit: boolean;
  isFumble: boolean;
  /**
   * Did it beat the target? Always false when the context carried no `dc`,
   * because there was nothing to beat — check `resolved.interpretation` for
   * whether the question was even asked.
   */
  success: boolean;
  /** Shaped for `addDiceRoll({ result })`, with the modifier already folded in. */
  result: DiceResult;
}

/**
 * The dice the active system resolves a check with.
 *
 * Throws when the system has no single expression — a dice pool's size depends
 * on the character, not the system — which is the honest answer for a tray that
 * needs to know what to render before it knows whose turn it is.
 */
export function systemCheckNotation(): string {
  const notation = checkNotation();
  if (!notation) {
    throw new Error(
      'The active game system does not resolve checks with a fixed dice expression, ' +
        'so the animated tray cannot roll one. Use resolveRoll() and display the result instead.',
    );
  }
  return notation;
}

/**
 * The system's check dice for UI copy — a tooltip, a button label.
 *
 * Never throws, because a label is not a roll and no piece of chrome should be
 * able to white-screen a character sheet. Null when the system has no single
 * expression, which the caller should render as words rather than dice.
 */
export function displayCheckNotation(): string | null {
  try {
    return checkNotation();
  } catch {
    return null;
  }
}

/** Whether the tray can animate this system's checks at all. */
export function systemSupportsAnimatedChecks(): boolean {
  try {
    return checkNotation() !== null;
  } catch {
    // No active system. Not a supported roll either way, and the caller asking
    // this question is deciding whether to render a button, not resolving dice.
    return false;
  }
}

/** `1d20` → `{ count: 1, sides: 20 }`. Null for anything else. */
function parseSingleDie(notation: string): { count: number; sides: number } | null {
  const match = /^(\d+)d(\d+)$/i.exec(notation.trim());
  if (!match) return null;
  return { count: Number(match[1]), sides: Number(match[2]) };
}

function formatModifier(modifier: number): string {
  if (modifier === 0) return '';
  return modifier > 0 ? `+${modifier}` : `${modifier}`;
}

/**
 * Roll a check on the 3D tray and let the system say what it meant.
 *
 * Returns null when the roll did not happen: the tray is not ready, the roll
 * was dropped, or the active system resolves checks with something the tray
 * cannot throw. Callers already handle that null, because it is the same signal
 * `useDiceStore.roll` has always given.
 *
 * WHY NULL AND NOT A THROW, when the rest of this work exists to make things
 * fail loudly: those two are not the same failure. Resolving a Trophy check as
 * 5e is a wrong answer nobody at the table can see, and that must throw.
 * Declining to roll dice the tray cannot render is a missing answer — and a
 * throw from a click handler lands as an unhandled rejection, which is loud in
 * a console the player does not have open and silent everywhere they are
 * looking. So this declines, and says why in the log.
 */
export async function rollSystemCheck(
  context: RollContext,
  options: { mode?: RollMode; label?: string } = {},
): Promise<AnimatedCheck | null> {
  const { mode = 'normal', label } = options;

  const dice = checkNotation();
  if (!dice) {
    console.error(
      '[system-roll] The active game system resolves checks with no fixed dice expression ' +
        '(a pool, step dice, or custom), so the animated tray has nothing to throw. ' +
        'This button should not have been offered — gate it on systemSupportsAnimatedChecks().',
    );
    return null;
  }
  const modifier = context.modifier ?? 0;
  const single = parseSingleDie(dice);
  const canSwing = mode !== 'normal' && single?.count === 1;

  const tray = useDiceStore.getState().roll;
  const thrown = await tray(canSwing ? `2d${single!.sides}` : dice);
  if (!thrown) return null;

  let natural: number;
  let advDisRolls: [number, number] | undefined;
  let discardedValue: number | undefined;

  if (canSwing) {
    const first = thrown.rolls[0]?.value ?? 0;
    const second = thrown.rolls[1]?.value ?? 0;
    natural = mode === 'advantage' ? Math.max(first, second) : Math.min(first, second);
    discardedValue = mode === 'advantage' ? Math.min(first, second) : Math.max(first, second);
    advDisRolls = [first, second];
  } else {
    // Multi-die expressions (2d6, 4dF) sum; the tray's own total is the natural
    // result before modifiers.
    natural = single?.count === 1 ? (thrown.rolls[0]?.value ?? 0) : thrown.total;
  }

  // The system reads the dice — including, deliberately, `advantage` on the
  // context it was given, so `roll.meta` records the swing even though the
  // choosing happened up here.
  const resolved = interpretRolled(context, natural);
  const base = `${dice}${formatModifier(modifier)}`;
  const notation = label ? `${base} (${label})` : base;

  return {
    resolved,
    natural,
    total: resolved.roll.total,
    notation,
    isCrit: resolved.criticality === 'critical-success',
    isFumble: resolved.criticality === 'critical-failure',
    success: succeeded(resolved.interpretation),
    result: {
      notation,
      rolls: canSwing ? [{ sides: single!.sides, value: natural }] : thrown.rolls,
      total: resolved.roll.total,
      // Carried so the chat card renders the system's verdict rather than
      // re-deriving one from the number of sides on the die.
      criticality: resolved.criticality,
      ...(advDisRolls ? { advDisRolls } : {}),
      ...(discardedValue !== undefined ? { discardedValue } : {}),
    },
  };
}
